<script setup lang="ts">
import {
  getRewardSummaryDetail,
  getRewardTypeIcon,
  getRewardTypeLabel,
  getRewardUsageTags
} from '@/components/rewards/utils/rewardHelpers'

import RewardUsageTag from '@/components/rewards/RewardUsageTag.vue'

import type { Icons } from '@/components/base/BaseIcon.vue'
import type { LoyaltyReward } from '@/types/types'

const props = withDefaults(
  defineProps<{
    reward: LoyaltyReward
    sideitemTopOffset?: string
  }>(),
  {
    sideitemTopOffset: 'top-6'
  }
)

const { t } = useI18n()

const rewardTypeLabel = computed(() => (props.reward?.rewardType ? getRewardTypeLabel(props.reward.rewardType, t) : ''))

const rewardTypeIcon = computed(() =>
  props.reward?.rewardType ? getRewardTypeIcon(props.reward.rewardType) : 'HeroGiftIconOutline'
)

const rewardSummaryItems = computed(() => (props.reward ? getRewardSummaryDetail(props.reward, t) : []))

const usageTags = computed(() => {
  if (!props.reward) return []
  return getRewardUsageTags(props.reward.usages ?? [], t)
})
</script>

<template>
  <Card :class="['overflow-hidden sticky w-full', sideitemTopOffset]">
    <CardContent class="space-y-5">
      <div class="rounded-lg bg-muted p-4">
        <div class="flex items-center gap-3">
          <img
            v-if="reward.pictureUrl"
            :src="reward.pictureUrl"
            :alt="reward.name"
            class="h-11 w-11 shrink-0 rounded-lg object-cover"
          />
          <div v-else class="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary/10">
            <BaseIcon name="HeroGiftIconOutline" class="h-5 w-5 text-primary" />
          </div>
          <div class="min-w-0 flex-1">
            <p class="text-sm font-medium leading-snug text-foreground">
              {{ reward.name || '—' }}
            </p>
            <BaseChip class="mt-1" :icon="rewardTypeIcon" :text="rewardTypeLabel" />
          </div>
        </div>

        <div v-if="rewardSummaryItems.length > 0" class="mt-3 border-t border-border/80 pt-3">
          <p class="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            {{ t('pages.offer.create.summary.detailTitle') }}
          </p>
          <ul class="space-y-1">
            <li
              v-for="item in rewardSummaryItems"
              :key="`${item.icon}-${item.text}`"
              class="flex items-start gap-2.5 text-xs leading-relaxed text-foreground-secondary"
            >
              <BaseIcon
                :name="item.icon as Icons"
                :class="item.icon?.startsWith('Iconoir') ? 'mt-[3px]' : 'mt-[5px]'"
                class="h-3.5 w-3.5 shrink-0 text-muted-foreground"
              />
              <span class="leading-[26px]">
                {{ item.text }}
              </span>
            </li>
          </ul>
        </div>
      </div>

      <div v-if="usageTags" class="rounded-lg bg-muted p-4">
        <p class="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {{ t('pages.reward.summary.usage') }}
        </p>
        <RewardUsageTag :usage-tags="usageTags" />
      </div>

      <slot name="additional-summary" />
    </CardContent>
  </Card>
</template>
